<template>
  <editor-content :editor="editor" class="content-editor fs-exclude"/>
</template>

<script setup lang="ts">
import { nextTick, watch } from 'vue'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import { wikiToPlainText } from '@/util/tokenize_wiki_text'
import ResourceNode from './ResourceNode'

// Displays and edits Tiptap HTML with one Tiptap instance, made editable while isEditMode is on.
const props = defineProps<{
  wikiText: string
  isEditMode: boolean
  placeHolder: string
  nameAttrs: Record<string, string>
  toEmbeddedText: (input: string) => string
  toWikiText: (output: string) => string
}>()

const emit = defineEmits<{
  draft: [plainText: string, wikiText: string]
  blurred: [relatedTarget: EventTarget | null]
}>()

const getWikiText = () => props.toWikiText(editor.value?.getHTML() || props.wikiText)
const getPlainText = () => wikiToPlainText(editor.value?.getText() || '')
const reportDraft = () => emit('draft', getPlainText(), getWikiText())

const editor = useEditor({
  autofocus: true,
  editable: false,
  editorProps: {
    attributes: { role: 'textbox', ...props.nameAttrs },
  },
  content: props.toEmbeddedText(props.wikiText),
  extensions: [
    StarterKit,
    Placeholder.configure({
      placeholder: props.placeHolder,
      showOnlyWhenEditable: false,
      emptyEditorClass: 'first:before:h-0 first:before:text-[#535353] first:before:italic first:before:float-left first:before:content-[attr(data-placeholder)] first:before:pointer-events-none',
    }),
    ResourceNode
  ],
  onBlur: ({ event }) => emit('blurred', event.relatedTarget),
  onUpdate: () => {
    if (props.isEditMode) reportDraft()
  }
})

watch(() => props.isEditMode, isEditMode => {
  editor.value?.setEditable(isEditMode)
  if (!isEditMode) return
  reportDraft()
  nextTick(() => editor.value?.commands.focus())
})

watch(() => props.wikiText, wikiText => {
  if (!props.isEditMode && editor.value && wikiText !== getWikiText()) {
    editor.value.commands.setContent(props.toEmbeddedText(wikiText))
  }
})

watch(() => props.placeHolder, placeHolder => {
  editor.value?.extensionManager.extensions.forEach(extension => {
    if (extension.name === 'placeholder') extension.options.placeholder = placeHolder
  })
})

// A caller can reuse a mounted editor instance under new nameAttrs (e.g. array-index keying
// after an earlier item is removed) — useEditor only reads props at setup, so push a later
// change into the live editor too (WCAG 2.5.3).
watch(() => props.nameAttrs, nameAttrs => {
  if (!editor.value) return
  editor.value.setOptions({
    editorProps: {
      ...editor.value.options.editorProps,
      attributes: { role: 'textbox', ...nameAttrs },
    },
  })
})

defineExpose({ editor, getPlainText })
</script>
